import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithRefresh } from './baseQuery';

export const osnovaApi = createApi({
  reducerPath: 'osnovaApi',
  baseQuery: baseQueryWithRefresh,
  // Дефолтное время жизни кэша после потери последней подписки.
  // RTK Query по умолчанию = 60 секунд. Для нашего интерфейса этого мало:
  // данные профиля (/auth/me), реестры договоров/претензий и т.п. редко
  // меняются между переходами между вкладками, но юрист видит lag при
  // каждом возврате на страницу, потому что fetch начинается заново.
  // 300с = 5 мин — кеш переживает обычные навигации, не даёт «прыгать»
  // содержимому. WS bridge инвалидирует кеш при реальных изменениях,
  // так что устаревших данных юзер не увидит.
  keepUnusedDataFor: 300,
  // refetchOnMountOrArgChange=false — при возврате на уже посещённую
  // страницу сразу показываем кеш, без ожидания свежего ответа.
  refetchOnMountOrArgChange: false,
  // refetchOnFocus=false — табы переключают часто, не дёргаем сервер
  // при каждом focus.
  refetchOnFocus: false,
  // refetchOnReconnect=true — а вот после потери и восстановления
  // соединения свежие данные нужны.
  refetchOnReconnect: true,
  tagTypes: [
    'KspProject',
    'KspSnapshot',
    'KspExport',
    'KadryInfo',
    // ОСНОВА.Кадры: заявки (карточка + 'LIST'), история статусов заявки,
    // версии профиля поиска (id = заявка), справочники, гейты, журнал.
    'KadryRequest',
    'KadryRequestHistory',
    'KadryProfile',
    'KadryWorkSite',
    'KadryGate',
    'KadryAudit',
    'KadryStaff',
    // Поиск, реестр кандидатов, предложения, подключения (Р10–Р13).
    'KadryRegistry',
    'KadrySearchRun',
    'KadryVacancy',
    'KadrySource',
    'KadryBot',
    'KadryProcessingProfile',
    'KadryMoney',
    'KadryNotification',
    // Сводка дашборда «Обзор».
    'KadryOverview',
    'MaterialNorm',
    // Объявление о технических работах (баннер «обновление через 5 минут»).
    'Maintenance',
    // ОСНОВА.Помощник: свои чаты сотрудника и сообщения чата (id = чат).
    'AssistantSession',
    'AssistantMessages',
    'Me',
    'Requests',
    'Knowledge',
    'Check',
    'Issues',
    'ChatSession',
    'ChatMessages',
    'CacheStats',
    'Contract',
    'ContractFiles',
    'ContractTemplate',
    'Counterparty',
    'CounterpartyDocuments',
    'CounterpartyRequisiteCheck',
    'Claim',
    'ClaimDocuments',
    'Lawsuit',
    'ComposedResponses',
    'Parties',
    'Correspondence',
    'DispatcherInbox',
    // Журнал шифров проектной корреспонденции ПОЛЮС (агент-диспетчер).
    'PolyusCodes',
    'Deadline',
    'Lna',
    'StatutTrash',
    'GovRequest',
    'NpaEvent',
    'AdminUsers',
    'AdminStats',
    'AdminAudit',
    'AuditSession',
    'VolumeCheck',
    'WriteOffCheck',
    'RegisterSession',
    'AccumLedger',
    'AccumUpload',
    'FgiscsPeriod',
    // «Нормативная база» Сметчика: редакции ФСНБ (id = редакция), реестр
    // дополнений ФГИС ЦС и сам справочник (поиск, карточки) — его содержимое
    // меняется, когда в действие вводят другую редакцию.
    'FsnbEdition',
    'FsnbRegistry',
    'FsnbBase',
    'RateCheck',
    'RateDiscrepancy',
    'KsAct',
    'KsDiscrepancy',
    // Пакет акта (КС-2/КС-3/КС-6а). Строки, находки и правки инвалидируются
    // отдельно от шапки: сборка идёт в очереди, и после неё меняются все три
    // среза сразу, а карточка пакета поллится чаще остальных.
    'KsPackage',
    'KsPackageLine',
    'KsPackageIssue',
    'KsPackageOverride',
    // Первичка пакета и выпущенные бланки: у них своя частота изменения —
    // загрузка первички не трогает состав строк, а выпуск форм не трогает
    // ни строки, ни находки сборки.
    'KsPackageDoc',
    'KsPackageForm',
    // Материалы строк: меняются загрузкой первички и пересборкой, но НЕ
    // выпуском форм — свой тег, чтобы таблица материалов не перечитывалась на
    // каждый рендер бланка (это сотни строк на акт).
    'KsPackageMaterial',
    'ContractEstimate',
    'TenderEstimate',
    'GsCheck',
    'GsDiscrepancy',
    'ZviCheck',
    'SmetaChatSession',
    'SmetaChatMessages',
    'EconRun',
    'EconAnomaly',
    'EconTemplate',
    'EconChatSession',
    'EconDictionary',
    'EconPlanVersion',
    // Хронум Экономиста (/agents/econ/chronum, миграция 0302): 'STATUS' — ключ,
    // последние снимки и идущие загрузки, 'POSITIONS' и 'SITES' — сопоставления,
    // 'HOURS' — свод месяца (любой месяц), 'SNAPSHOTS' — история загрузок,
    // 'COST_CENTERS' — используемые коды, 'LOAD:<id>' — ход загрузки,
    // 'HISTORY:<position|site>:<ключ>' — журнал правок сопоставления.
    'EconChronum',
    // Справочник ставок НДС — общий для сметы и экономиста (миграция 0225).
    'VatRate',
    // Справочник техники платформы (/fleet, миграция 0296): id = 'LIST' —
    // список моделей, 'CATEGORIES' — категории, id модели — карточка и журнал.
    'Fleet',
    // Группы и ставки (миграция 0298). FleetGroup: id = 'LIST' — список групп,
    // id группы — карточка. EconGroupRate: 'LIST' — ставки на дату и история,
    // 'OBJECTS' — объекты.
    'FleetGroup',
    'EconGroupRate',
    // ПРЦ машино-часа (/agents/econ/prc, миграция 0297): 'LIST' — список версий,
    // 'CURRENT' — действующие ПРЦ, `head:<id>` — версия за базовый год, id версии —
    // всё, что от неё зависит (другие годы, модель, «было → стало», лимиты).
    'EconPrc',
    // Локальные сметы договора (эталон мира B) и их позиции.
    'ContractLsr',
    'MailboxRoute',
    'MailboxCategory',
    'DispatcherForward',
    'PtoProject',
    'PtoRdDocument',
    'PtoRdRevision',
    // План переноса привязок Прораба на новую ревизию РД (rd-carry-over).
    'PtoRdCarryOver',
    'PtoRdImport',
    'PtoVolume',
    // Анализ комплектности РД (№7): вкладка ревизии и правила типового комплекта.
    'PtoRdGaps',
    'PtoRdGapRule',
    'PtoAct',
    'PtoExecAct',
    'PtoSmrRegister',
    'PtoSmrItem',
    'PtoOzhr',
    'PtoKsDraft',
    'PtoOrderRep',
    // Реквизиты шапки АОСР проекта (стороны, объект, представители по умолчанию):
    // их берут и форма «Актов ИД», и сборка АОСР по заявке Прораба.
    'PtoAosrRequisites',
    'PtoQualityMat',
    'PtoActTemplate',
    'PtoPprTk',
    'PtoPprTkDraft',
    'PtoPprFigure',
    // Справочник техники организации (ППР по РД) — общий для всех проектов ПТО.
    'PtoEquipment',
    'PtoVolumeCheck',
    'PtoVolumeDiscrepancy',
    'PtoRequest',
    'PtoSq',
    'PtoInspection',
    'PtoIdPackage',
    'PtoChecklist',
    'PtoLetterTemplate',
    'PtoChatSession',
    'PtoChatMessages',
    // КФО (контроль физических объёмов Заказчика): строки среза, наши отчёты,
    // журнал RFI, гарантийные обязательства, история импортов, привязки договоров.
    'KfoLines',
    'KfoReports',
    'KfoRfi',
    'KfoGuarantees',
    'KfoImports',
    'KfoBindings',
    'Mto',
    'MtoRun',
    // Журнал «Приход ТМЦ»: строки, свод, объекты прихода.
    'MtoReceipts',
    // БДР v2 Экономиста: сетка периодов, версия, трассировка.
    'EconBdr',
    'MtoNomenclature',
    'MtoSupplier',
    'MtoRdLibrary',
    'Meetings',
    'MeetingProtocol',
    'MeetingAssignments',
    'MyMeetingAssignments',
    'MeetingParticipants',
    'MeetingDepartments',
    'MeetingEmployees',
    'LogistDictionary',
    'LogistSigner',
    'LogistOrder',
    'LogistSeries',
    'LogistTrip',
    'LogistWeighing',
    'LogistEtrn',
    'LogistInbox',
    'LogistEdoBox',
    'LogistEvidence',
    'LogistTask',
    'MtoLot',
    'MtoQualityDoc',
    'MtoApplication',
    'MtoValidity',
    'MtoDeficit',
    'Progress',
    'ProgressRun',
    // Дашборд объекта Руководителя: список объектов, показатели и привязки (id = объект).
    'ProgressObject',
    // Поручения Руководителя: реестр и «Мои поручения» исполнителя.
    'ProgressInstruction',
    // ОСНОВА.Эксперт (чат руководителя): список чатов, реплики чата (id = чат),
    // расход за месяц и расход руководителей для администратора.
    'AdvisorSession',
    'AdvisorMessages',
    'AdvisorUsage',
    // ОСНОВА.Геодезист: справочники агента (info/objects/tolerances), карточка
    // ИС (id) и реестр ИС + счётчики бейджей; точки прилипания листа (id = ИС);
    // оси объекта ИС (id = ИС).
    'Geo',
    'GeoScheme',
    'GeoSchemes',
    'GeoSheetSnaps',
    'GeoAxisGrids',
    'Field',
    'Announcement',
    'AdminAnnouncement',
    'PrCandidate',
    'PublishedPr',
    'RiskNotification',
    'PlatformNotification',
    'OrgService',
    'ContractHandoff',
    'CeoDashboard',
    'CeoBankAccount',
    'CeoBankConnection',
    'CeoPayment',
    'CeoDebt',
    'CeoInflow',
    'CeoApproval',
    'FinancierDashboard',
    'FinancierRequest',
    'FinancierPayrollBatch',
    'FinancierChronumSite',
    'FinancierAdvanceRetention',
    'FinancierTaxCalendar',
    'FinancierRegistry',
    'FinancierCounterparty',
    // «Заявки на оплату» сотрудников (/pay-requests, миграция 0332): 'LIST' —
    // списки, 'META' — справочники формы, id заявки — карточка.
    'PayRequest',
    // ОСНОВА.Диск: 'INFO', 'SPACES', `node:<id>`, `children:<папка>`,
    // `preview:<id>`, `versions:<id>`, `acl:<id>`, `audit:<id>`, 'TRASH',
    // 'RECENT', 'FAVORITES', 'SHARED', 'SEARCH'. Живые обновления перечитывают
    // папку инвалидацией `children:<папка>`.
    'Disk',
  ],
  endpoints: () => ({}),
});
